import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'store/hooks';
import { acknowledgeConsent } from 'store/slices/documentSlice';
import { AuthContext } from 'utils/AuthContext';
import { ROUTE } from 'route/RouteEnums';
import EmbeddedSign from 'components/Documents/EmbeddedSign';
import FinishingUpOverlay from 'components/FinishingUpOverlay';

interface PolicyConsentState {
  signLink?: string;
}

const MIN_FINISHING_OVERLAY_MS = 5000;

const PolicyConsent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const state = location.state as PolicyConsentState | undefined;
  const [showFinishingOverlay, setShowFinishingOverlay] = useState(false);

  useEffect(() => {
    if (!state?.signLink) {
      navigate(ROUTE.DASHBOARD, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.signLink]);

  if (!state?.signLink) {
    return null;
  }

  const backToDashboard = () => navigate(ROUTE.DASHBOARD);

  const handleCompleted = () => {
    if (user?.patientId) {
      setShowFinishingOverlay(true);
      const minFloor = new Promise<void>((resolve) => setTimeout(resolve, MIN_FINISHING_OVERLAY_MS));
      const acknowledged = dispatch(acknowledgeConsent(user.patientId))
        .unwrap()
        .catch(() => undefined); // swallow: navigate regardless of outcome, matching today's behavior
      Promise.all([acknowledged, minFloor]).then(() => {
        setShowFinishingOverlay(false);
        backToDashboard();
      });
    } else {
      backToDashboard();
    }
  };

  const handleDeclined = () => {
    toast('You declined the document. You can sign anytime from your dashboard.', {
      type: 'info',
      icon: true,
    });
    backToDashboard();
  };

  const handleFailed = () => {
    toast('Signing failed. Please try again.', { type: 'error', icon: true });
    backToDashboard();
  };

  return (
    <Box sx={{ bgcolor: '#F7F9FC', p: { xs: 2, md: 4 } }}>
      <FinishingUpOverlay open={showFinishingOverlay} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h2'>Policies & Consents</Typography>
        <IconButton onClick={backToDashboard} aria-label='Back to dashboard'>
          <CloseIcon />
        </IconButton>
      </Box>
      <EmbeddedSign
        signLink={state.signLink}
        onCompleted={handleCompleted}
        onDeclined={handleDeclined}
        onFailed={handleFailed}
      />
    </Box>
  );
};

export default PolicyConsent;
